import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const params = {};
        if (entityFilter !== 'all') params.entity_type = entityFilter;
        const { data } = await api.get('/audit-logs', { params });
        setLogs(data);
      } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    };
    fetchLogs();
  }, [entityFilter]);

  const actionColors = {
    create: 'bg-brand-success/15 text-brand-success',
    update: 'bg-blue-100 text-blue-700',
    delete: 'bg-brand-error/15 text-brand-error',
    login: 'bg-amber-100 text-amber-700',
    approve: 'bg-brand-success/15 text-brand-success',
    calculate: 'bg-purple-100 text-purple-700',
    settle: 'bg-brand-primary/15 text-brand-primary',
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Audit Logs</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Track all system actions for compliance</p>
        </div>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-48 bg-white border-brand-border rounded-sm" data-testid="audit-filter">
            <SelectValue placeholder="Filter by entity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="user">Users</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="branch">Branches</SelectItem>
            <SelectItem value="fund_transaction">Funds</SelectItem>
            <SelectItem value="pnl_entry">P&L</SelectItem>
            <SelectItem value="performance_fee">Performance</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
            <SelectItem value="trading_account">Accounts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Timestamp', 'User', 'Action', 'Entity', 'Details', 'IP'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={6} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 logs.length === 0 ? <tr><td colSpan={6} className="py-12 text-center text-brand-text-secondary">No audit logs found</td></tr> :
                 logs.map((log, i) => (
                  <tr key={log.id || log._id || `${log.created_at}-${i}`} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 text-brand-text-secondary whitespace-nowrap">{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
                    <td className="py-3 px-4 text-brand-text-primary">{log.user_name || '-'}</td>
                    <td className="py-3 px-4">
                      <Badge className={`text-xs border-0 capitalize ${actionColors[log.action] || 'bg-gray-100 text-gray-600'}`}>{log.action}</Badge>
                    </td>
                    <td className="py-3 px-4 text-brand-text-secondary capitalize">{log.entity_type?.replace('_', ' ') || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary max-w-[300px] truncate">{log.details || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary text-xs">{log.ip_address || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
